import React, { useMemo, useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import CoachesCornerComments from '../../components/Comments/CoachesCornerComments.jsx';
import Tabs from '../../../../ui/canon/Tabs.jsx';
import Button from '../../../../ui/canon/Button.jsx';
import Modal from '../../../../ui/canon/Modal.jsx';
import { ChatBubbleLeftRightIcon, UserCircleIcon, ArrowLeftIcon, EyeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const PROGRAMS_QUERY = gql`
  query SprintPrograms {
    sprintPrograms {
      id title slug status enrollment_count duration_days level price_cents
      coachProfile { 
        id
        user {
          id
        }
      }
    }
  }
`;

const CURRENT_USER_QUERY = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
    }
  }
`;

const ENROLLMENTS_QUERY = gql`
  query SprintEnrollments($coach_profile_id: ID) {
    sprintEnrollments(coach_profile_id: $coach_profile_id) {
      id
      status
      started_on
      target_end_on
      progress_pct
      coach_notes
      valuation_validated_at
      valuation_validation_notes
      valuation_validated
      user_has_saved_valuation
      user {
        id
        allowed_name
        user_name
        name
        profile_picture
        student_avatar_url
      }
      sprintProgram {
        id
        title
        slug
        provides_valuation_validation
      }
      sprint {
        id
        comment_count
        unread_comment_count
        comments(limit: 1) {
          id
          comment
          created_at
          user {
            id
            allowed_name
            name
          }
        }
        items {
          id
          status
          coach_review_status
        }
      }
    }
  }
`;

const VALIDATE_VALUATION_MUTATION = gql`
  mutation ValidateValuation($enrollmentId: ID!, $notes: String) {
    validate_valuation(enrollmentId: $enrollmentId, notes: $notes) {
      enrollment {
        id
        valuation_validated_at
        valuation_validation_notes
        valuation_validated
      }
      errors
    }
  }
`;

const resolveAvatarSrc = (val) => {
  if (!val) return null;
  if (val.startsWith('http') || val.startsWith('/')) return val;
  return `https://s3-us-west-2.amazonaws.com/findbob-production/images/${val}`;
};

const AvatarWithFallback = ({ user: u, size = 'w-10 h-10', className = '' }) => {
  const [failed, setFailed] = React.useState(false);
  const src = resolveAvatarSrc(u?.student_avatar_url || u?.profile_picture);
  const name = u?.name || u?.allowed_name || u?.user_name || 'U';
  const initial = name.charAt(0).toUpperCase();

  if (src && !failed) {
    return <img alt={name} src={src} onError={() => setFailed(true)} className={`${size} rounded-full object-cover ${className}`} />;
  }
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold ${className}`}>
      {initial}
    </div>
  );
};

export default function CoachPortalDashboardContainer() {
  const { data: programsData, loading: programsLoading, error: programsError } = useQuery(PROGRAMS_QUERY);
  const { data: userData, loading: userLoading } = useQuery(CURRENT_USER_QUERY);
  const programs = programsData?.sprintPrograms || [];
  const currentUserId = userData?.getCurrentUser?.id;
  
  // Filter programs to only those owned by the current user's coach profile
  const userPrograms = useMemo(() => {
    if (!currentUserId) return [];
    return programs.filter(p => p.coachProfile?.user?.id === currentUserId);
  }, [programs, currentUserId]);
  
  // Get coach profile ID from the current user's programs
  const coachProfileId = useMemo(() => {
    const profileIds = userPrograms
      .map(p => p.coachProfile?.id)
      .filter(Boolean);
    return profileIds.length > 0 ? profileIds[0] : null;
  }, [userPrograms]);
  
  const { data: enrollmentsData, loading: enrollmentsLoading, error: enrollmentsError } = useQuery(
    ENROLLMENTS_QUERY,
    {
      variables: { coach_profile_id: coachProfileId },
      skip: !coachProfileId,
    }
  );
  
  const allEnrollments = enrollmentsData?.sprintEnrollments || [];
  // Show all enrollments (prototype shows "Active Participants" as header but displays all)
  const enrollments = allEnrollments;
  const [tab, setTab] = useState('programs');
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  
  // Validation modal state
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationEnrollmentId, setValidationEnrollmentId] = useState(null);
  const [validationNotes, setValidationNotes] = useState('');
  const [validateValuation, { loading: validationLoading }] = useMutation(VALIDATE_VALUATION_MUTATION, {
    refetchQueries: [{ query: ENROLLMENTS_QUERY, variables: { coach_profile_id: coachProfileId } }],
  });
  
  const handleOpenValidationModal = (enrollmentId) => {
    setValidationEnrollmentId(enrollmentId);
    setValidationNotes('');
    setValidationModalOpen(true);
  };
  
  const handleValidateValuation = async () => {
    if (!validationEnrollmentId) return;
    try {
      const { data } = await validateValuation({
        variables: {
          enrollmentId: validationEnrollmentId,
          notes: validationNotes || null,
        },
      });
      if (data?.validate_valuation?.errors?.length > 0) {
        alert('Error: ' + data.validate_valuation.errors.join(', '));
      } else {
        setValidationModalOpen(false);
        setValidationEnrollmentId(null);
        setValidationNotes('');
      }
    } catch (err) {
      alert('Error validating valuation: ' + err.message);
    }
  };
  
  // Get coach profile ID for the profile link (reuse existing calculation)
  const metrics = useMemo(() => {
    const totalUnread = enrollments.reduce((sum, e) => sum + (e.sprint?.unread_comment_count || 0), 0);
    return {
      activePrograms: userPrograms.filter((p) => p.status === 'published').length,
      activeParticipants: userPrograms.reduce((n, p) => n + (p.enrollment_count || 0), 0),
      completionRate: 67,
      unreadMessages: totalUnread,
    };
  }, [userPrograms, enrollments]);

  const loading = programsLoading || userLoading || ((tab === 'participants' || tab === 'messages') && enrollmentsLoading);
  const error = programsError || ((tab === 'participants' || tab === 'messages') && enrollmentsError);

  return (
    <SingleColumnTemplate title="Coach Portal" subtitle="Manage your programs and participants" variant="wide">
      {/* Header actions */}
      <div className="flex items-center justify-end gap-2 mb-4">
        {/* Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            className="inline-flex items-center gap-2 rounded-lg border border-succession-border bg-succession-bg-primary text-succession-text-primary px-3 py-2 hover:bg-succession-hover-bg focus:outline-none focus:ring-2 focus:ring-succession-focus-ring"
          >
            <UserCircleIcon className="w-4 h-4" />
            Profile
          </button>
          {profileDropdownOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setProfileDropdownOpen(false)}
              />
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 rounded-lg border border-succession-border bg-succession-bg-primary shadow-lg z-20">
                <div className="py-1">
                  <a
                    href="/coach/profile/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-succession-text-primary hover:bg-succession-hover-bg"
                    onClick={() => setProfileDropdownOpen(false)}
                  >
                    <ArrowLeftIcon className="w-4 h-4 rotate-180" />
                    Profile Settings
                  </a>
                  {coachProfileId && (
                    <a
                      href={`/coaches/${coachProfileId}`}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-succession-text-primary hover:bg-succession-hover-bg"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <EyeIcon className="w-4 h-4" />
                      View Public Profile
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <Button
          as="a"
          href="/coach/programs/new"
          variant="primary"
          size="md"
          onClick={() => {
            try {
              if (window.ahoy && typeof window.ahoy.track === 'function') {
                window.ahoy.track('coach_portal_create_program_click');
              }
            } catch (_) {}
          }}
        >
          + Create Program
        </Button>
      </div>
      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-5">
          <div className="text-sm text-succession-text-secondary mb-2">Active Programs</div>
          <div className="text-3xl font-bold">{metrics.activePrograms}</div>
        </div>
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-5">
          <div className="text-sm text-succession-text-secondary mb-2">Active Participants</div>
          <div className="text-3xl font-bold">{metrics.activeParticipants}</div>
        </div>
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-5">
          <div className="text-sm text-succession-text-secondary mb-2">Completion Rate</div>
          <div className="text-3xl font-bold">{metrics.completionRate}%</div>
        </div>
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-5">
          <div className="text-sm text-succession-text-secondary mb-2">Unread Messages</div>
          <div className="text-3xl font-bold">{metrics.unreadMessages}</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={[
          { id: 'programs', label: 'My Programs' },
          { id: 'participants', label: 'Participants' },
          { id: 'messages', label: 'Messages' },
        ]}
        value={tab}
        onChange={setTab}
        showDescription={false}
        variant="outline"
        size="md"
        className="mb-4"
      />

      {loading && <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6">Loading...</div>}
      {error && <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6">Error loading</div>}

      {!loading && !error && tab === 'programs' && (
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-succession-text-secondary">Your Programs</div>
            <Button
              as="a"
              href="/coach/programs/new"
              variant="outline"
              size="sm"
              onClick={() => {
                try {
                  if (window.ahoy && typeof window.ahoy.track === 'function') {
                    window.ahoy.track('coach_portal_new_program_click');
                  }
                } catch (_) {}
              }}
            >
              + New Program
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {userPrograms.map((p) => (
              <div key={p.id} className="rounded-xl border border-succession-border bg-succession-bg-primary p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{p.title}</div>
                  <span className="rounded px-2 py-0.5 text-xs border border-succession-border">{p.status}</span>
                </div>
                <div className="text-sm text-succession-text-secondary">
                  {p.duration_days} days • {p.level}
                </div>
                <div className="text-sm">Enrollments: <span className="font-medium">{p.enrollment_count || 0}</span></div>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    as="a"
                    href={`/coach/programs/${p.id}/edit`}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-center border-succession-primary text-succession-primary hover:bg-succession-primary-10"
                    onClick={() => {
                      try {
                        if (window.ahoy && typeof window.ahoy.track === 'function') {
                          window.ahoy.track('coach_portal_edit_program_click', { program_id: p.id });
                        }
                      } catch (_) {}
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    as="a"
                    href={`/sprints/programs/${p.slug}`}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-center border-succession-primary text-succession-primary hover:bg-succession-primary-10"
                    onClick={() => {
                      try {
                        if (window.ahoy && typeof window.ahoy.track === 'function') {
                          window.ahoy.track('coach_portal_view_program_click', { program_id: p.id, slug: p.slug });
                        }
                      } catch (_) {}
                    }}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
            {userPrograms.length === 0 && <div className="text-succession-text-secondary">No programs yet</div>}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'participants' && (
        <div className="rounded-2xl border border-succession-border bg-succession-bg-primary p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Active Participants</h2>
            <div className="flex gap-2">
              <button className="rounded-lg border border-succession-border bg-succession-bg-primary px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]">
                Filter
              </button>
            </div>
          </div>
          {enrollments.length === 0 ? (
            <div className="rounded-xl border border-succession-border bg-succession-bg-primary p-12 text-center text-succession-text-secondary">
              No participants yet. Share your programs to get enrollments!
            </div>
          ) : (
            <div className="space-y-3">
              {enrollments.map((enrollment) => {
                // Use name directly for coaches (they should see participant names)
                const userName = enrollment.user?.name || enrollment.user?.allowed_name || enrollment.user?.user_name || 'Unknown User';
                const userInitial = userName.charAt(0).toUpperCase();
                const statusBadgeClass = 
                  enrollment.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : enrollment.status === 'completed'
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                      : enrollment.status === 'paused'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        : enrollment.status === 'cancelled'
                          ? 'bg-succession-bg-secondary text-succession-text-secondary'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
                
                const daysRemaining = enrollment.target_end_on
                  ? Math.ceil(
                      (new Date(enrollment.target_end_on).getTime() - new Date().getTime()) /
                        (1000 * 60 * 60 * 24)
                    )
                  : null;

                // Calculate progress from sprint tasks (same logic as EnrollmentWorkspaceContainer)
                const tasks = enrollment.sprint?.items || [];
                const totalTasks = tasks.length;
                
                // For coach-led sprints, count effectively completed (approved or no review required)
                const completedTasks = totalTasks > 0 ? tasks.filter(t => {
                  const status = String(t.status).toLowerCase();
                  if (status !== 'completed') return false;
                  // If no coach_review_status, it's completed (no review required)
                  if (!t.coach_review_status) return true;
                  // Only count if approved
                  return String(t.coach_review_status).toLowerCase() === 'approved';
                }).length : 0;
                
                const inProgressTasks = tasks.filter(t => String(t.status).toLowerCase() === 'in_progress').length;
                const pendingReviewTasks = tasks.filter(t => {
                  const status = String(t.status).toLowerCase();
                  const reviewStatus = t.coach_review_status ? String(t.coach_review_status).toLowerCase() : null;
                  return status === 'completed' && reviewStatus === 'pending';
                }).length;
                
                // Progress includes completed, in progress, and pending review
                const progressTasks = completedTasks + inProgressTasks + pendingReviewTasks;
                const calculatedProgressPct = totalTasks > 0 ? Math.round((progressTasks / totalTasks) * 100) : 0;

                return (
                  <div
                    key={enrollment.id}
                    className="rounded-lg border border-succession-border bg-succession-bg-primary hover:bg-[var(--succession-hover-bg)] transition-colors"
                  >
                    <div className="flex items-start gap-4 p-4">
                      {/* Avatar */}
                      <AvatarWithFallback user={enrollment.user} className="flex-shrink-0" />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold">{userName}</h4>
                            <p className="text-sm text-succession-text-secondary">
                              {enrollment.sprintProgram?.title || 'Unknown Program'}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs capitalize ${statusBadgeClass}`}>
                            {enrollment.status}
                          </span>
                        </div>

                        {/* Dates */}
                        {enrollment.started_on && (
                          <div className="flex items-center justify-between text-xs text-succession-text-secondary mb-2">
                            <span>
                              Started {new Date(enrollment.started_on).toLocaleDateString()}
                            </span>
                            {daysRemaining !== null && (
                              <span>{daysRemaining > 0 ? `${daysRemaining} days remaining` : daysRemaining === 0 ? 'Ends today' : 'Overdue'}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar - Full Width */}
                    <div className="px-4 pb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-succession-text-secondary">Progress</span>
                        <span className="font-medium">{calculatedProgressPct}%</span>
                      </div>
                      <div className="w-full h-2 bg-succession-bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-succession-primary rounded-full transition-all"
                          style={{ width: `${Math.min(100, Math.max(0, calculatedProgressPct))}%` }}
                        />
                      </div>
                    </div>

                    {/* Coach Notes and Actions */}
                    <div className="px-4 pb-4">
                      {/* Coach Notes */}
                      {enrollment.coach_notes && (
                        <div className="mb-3 p-3 bg-succession-bg-secondary rounded-lg">
                          <p className="text-sm">
                            <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {enrollment.coach_notes}
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <a
                          href={`/sprints/enrollments/${enrollment.id}?viewAsCoach=true`}
                          className="rounded-lg border border-succession-border bg-succession-bg-primary px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] inline-flex items-center gap-2"
                          onClick={() => {
                            try {
                              if (window.ahoy && typeof window.ahoy.track === 'function') {
                                window.ahoy.track('coach_portal_view_participant_progress', { enrollment_id: enrollment.id });
                              }
                            } catch (_) {}
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Progress
                        </a>
                        <button
                          className="rounded-lg border border-succession-border bg-succession-bg-primary px-3 py-1.5 text-sm hover:bg-[var(--succession-hover-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)] inline-flex items-center gap-2"
                          onClick={() => {
                            try {
                              if (window.ahoy && typeof window.ahoy.track === 'function') {
                                window.ahoy.track('coach_portal_message_participant', { enrollment_id: enrollment.id });
                              }
                            } catch (_) {}
                            // TODO: Implement messaging
                            alert('Messaging coming soon');
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Message
                        </button>
                        {/* View Valuation Button - only for validation programs with saved valuations */}
                        {enrollment.sprintProgram?.provides_valuation_validation && enrollment.user_has_saved_valuation && (
                          <a
                            href={`/coach/participant/${enrollment.id}/valuation`}
                            className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 inline-flex items-center gap-2"
                          >
                            <EyeIcon className="w-4 h-4" />
                            View Valuation
                          </a>
                        )}
                        {/* Validate Valuation Button - only for validation programs */}
                        {enrollment.sprintProgram?.provides_valuation_validation && (
                          enrollment.valuation_validated ? (
                            <span className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 inline-flex items-center gap-2">
                              <ShieldCheckIcon className="w-4 h-4" />
                              Validated {enrollment.valuation_validated_at && new Date(enrollment.valuation_validated_at).toLocaleDateString()}
                            </span>
                          ) : enrollment.user_has_saved_valuation ? (
                            <button
                              className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 inline-flex items-center gap-2"
                              onClick={() => handleOpenValidationModal(enrollment.id)}
                            >
                              <ShieldCheckIcon className="w-4 h-4" />
                              Validate Valuation
                            </button>
                          ) : (
                            <span
                              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-400 inline-flex items-center gap-2 cursor-not-allowed"
                              title="Participant must save their valuation before it can be validated"
                            >
                              <ShieldCheckIcon className="w-4 h-4" />
                              No Saved Valuation
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'messages' && (
        <CoachMessagesTab enrollments={enrollments} />
      )}

      {/* Validation Modal */}
      <Modal
        open={validationModalOpen}
        onClose={() => setValidationModalOpen(false)}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: 'linear-gradient(to bottom right, #10B981, #0D9488)',
              }}
            >
              <ShieldCheckIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-succession-text-primary">
                Validate Valuation
              </h2>
              <p className="text-sm text-succession-text-secondary">
                Review and confirm participant's valuation
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: '#ECFDF5',
              borderColor: '#A7F3D0',
            }}
          >
            <p className="text-sm" style={{ color: '#065F46' }}>
              By validating this valuation, you confirm that you have reviewed the participant's 
              inputs and their business value estimate is reasonable based on the data provided.
            </p>
          </div>
          
          <div>
            <label htmlFor="validation-notes" className="block text-sm font-medium text-succession-text-primary mb-2">
              Validation Notes <span className="text-succession-text-secondary font-normal">(optional)</span>
            </label>
            <textarea
              id="validation-notes"
              rows={4}
              className="w-full rounded-lg border border-succession-border bg-succession-bg-primary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-succession-focus-ring resize-none"
              placeholder="Add any notes or recommendations for the participant..."
              value={validationNotes}
              onChange={(e) => setValidationNotes(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-succession-text-secondary">
              These notes will be visible to the participant when they view their validated status.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-succession-border bg-succession-bg-secondary rounded-b-lg flex justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-succession-border bg-succession-bg-primary px-4 py-2 text-sm font-medium text-succession-text-primary hover:bg-succession-hover-bg transition-colors"
            onClick={() => setValidationModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
            style={{
              background: 'linear-gradient(to right, #059669, #0D9488)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(to right, #047857, #0F766E)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(to right, #059669, #0D9488)';
            }}
            onClick={handleValidateValuation}
            disabled={validationLoading}
          >
            <ShieldCheckIcon className="w-4 h-4" />
            {validationLoading ? 'Validating...' : 'Mark as Validated'}
          </button>
        </div>
      </Modal>
    </SingleColumnTemplate>
  );
}

// Coach Messages Tab Component
function CoachMessagesTab({ enrollments }) {
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter enrollments by search query
  const filteredEnrollments = useMemo(() => {
    if (!searchQuery.trim()) return enrollments;
    const query = searchQuery.toLowerCase();
    return enrollments.filter(e => {
      const userName = (e.user?.name || e.user?.allowed_name || e.user?.user_name || '').toLowerCase();
      const programName = (e.sprintProgram?.title || '').toLowerCase();
      const lastComment = e.sprint?.comments?.[0]?.comment?.toLowerCase() || '';
      return userName.includes(query) || programName.includes(query) || lastComment.includes(query);
    });
  }, [enrollments, searchQuery]);

  // Sort by last comment date (most recent first)
  const sortedEnrollments = useMemo(() => {
    return [...filteredEnrollments].sort((a, b) => {
      const aTime = a.sprint?.comments?.[0]?.created_at || '1970-01-01T00:00:00Z';
      const bTime = b.sprint?.comments?.[0]?.created_at || '1970-01-01T00:00:00Z';
      return new Date(bTime) - new Date(aTime);
    });
  }, [filteredEnrollments]);

  // Auto-select first enrollment if none selected
  React.useEffect(() => {
    if (!selectedEnrollmentId && sortedEnrollments.length > 0) {
      setSelectedEnrollmentId(sortedEnrollments[0].id);
    }
  }, [selectedEnrollmentId, sortedEnrollments]);

  const selectedEnrollment = sortedEnrollments.find(e => e.id === selectedEnrollmentId);

  // Calculate total unread count
  const totalUnread = useMemo(() => {
    return enrollments.reduce((sum, e) => sum + (e.sprint?.unread_comment_count || 0), 0);
  }, [enrollments]);

  const formatDate = (iso) => {
    try {
      const date = new Date(iso);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch (_) {
      return '';
    }
  };

  return (
    <div className="rounded-2xl border border-succession-border bg-succession-bg-primary overflow-hidden">
      <div className="flex h-[600px]">
        {/* Participants List - Left Panel */}
        <div className="w-80 border-r border-succession-border bg-succession-bg-secondary flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-succession-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-succession-text-primary">Participants</h3>
              {totalUnread > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-succession-primary text-white">
                  {totalUnread}
                </span>
              )}
            </div>
            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search participants..."
              className="w-full px-3 py-2 rounded-md bg-succession-bg-primary border border-succession-border text-succession-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-succession-focus-ring"
            />
          </div>

          {/* Participants List */}
          <div className="flex-1 overflow-y-auto">
            {sortedEnrollments.length === 0 ? (
              <div className="p-4 text-center text-succession-text-secondary text-sm">
                {searchQuery ? 'No participants found' : 'No participants yet'}
              </div>
            ) : (
              sortedEnrollments.map((enrollment) => {
                const userName = enrollment.user?.name || enrollment.user?.allowed_name || enrollment.user?.user_name || 'Unknown User';
                const userInitial = userName.charAt(0).toUpperCase();
                const lastComment = enrollment.sprint?.comments?.[0];
                const unreadCount = enrollment.sprint?.unread_comment_count || 0;
                const isSelected = enrollment.id === selectedEnrollmentId;

                return (
                  <button
                    key={enrollment.id}
                    onClick={() => setSelectedEnrollmentId(enrollment.id)}
                    className={`w-full p-3 border-b border-succession-border text-left hover:bg-succession-hover-bg transition-colors ${
                      isSelected ? 'bg-succession-bg-primary border-l-2 border-l-succession-primary' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <AvatarWithFallback user={enrollment.user} className="flex-shrink-0" />

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-succession-text-primary text-sm truncate">{userName}</span>
                          {unreadCount > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-succession-primary text-white text-xs font-medium flex-shrink-0">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-succession-text-secondary truncate mb-1">
                          {enrollment.sprintProgram?.title || 'Unknown Program'}
                        </p>
                        {lastComment && (
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-succession-text-secondary truncate flex-1">
                              {lastComment.user?.allowed_name || lastComment.user?.name}: {lastComment.comment}
                            </p>
                            <span className="text-xs text-succession-text-secondary flex-shrink-0">
                              {formatDate(lastComment.created_at)}
                            </span>
                          </div>
                        )}
                        {!lastComment && (
                          <p className="text-xs text-succession-text-secondary italic">No messages yet</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Messages Panel - Right Panel */}
        <div className="flex-1 flex flex-col bg-succession-bg-primary">
          {selectedEnrollment && selectedEnrollment.sprint?.id ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-succession-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AvatarWithFallback user={selectedEnrollment.user} />
                    <div>
                      <h3 className="font-semibold text-succession-text-primary">
                        {selectedEnrollment.user?.name || selectedEnrollment.user?.allowed_name || selectedEnrollment.user?.user_name || 'Unknown User'}
                      </h3>
                      <p className="text-sm text-succession-text-secondary">
                        {selectedEnrollment.sprintProgram?.title || 'Unknown Program'}
                      </p>
                    </div>
                  </div>
                  {/* Comment Count Indicator */}
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon 
                      className={`w-5 h-5 ${(selectedEnrollment.sprint?.unread_comment_count || 0) > 0 ? 'text-succession-primary' : 'text-succession-text-secondary'}`}
                    />
                    <span className={(selectedEnrollment.sprint?.unread_comment_count || 0) > 0 ? 'text-succession-primary font-medium text-sm' : 'text-succession-text-secondary text-sm'}>
                      {selectedEnrollment.sprint?.comment_count || 0}
                    </span>
                    {(selectedEnrollment.sprint?.unread_comment_count || 0) > 0 && (
                      <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-succession-primary rounded-full">
                        {selectedEnrollment.sprint.unread_comment_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <CoachesCornerComments
                  itemId={selectedEnrollment.sprint.id}
                  itemType="Sprint"
                  maxHeight="calc(100% - 60px)"
                  className="h-full flex flex-col border-0 rounded-none"
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-succession-text-secondary">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-sm">Select a participant to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


